import { useState, useEffect, useMemo } from 'react'
import { SECTIONS } from './Layout'
import { permisosPorPersonaApi } from '../api/menuPermisos'
import { colors, text, space, radius, sx } from '../design/tokens'

/**
 * SISTEMA → Menú por rol → Permisos por persona (Martin, 28 sep 2026: "una nueva
 * capa de permisos por usuario: conciliación de tarjetas se lo quiero dar solo a
 * Fátima y a Brenda, y también a Diego Gorfinkiel").
 *
 * Un ítem con personas asignadas queda RESTRINGIDO: lo ven y lo usan solo esas
 * personas, más administradores y super admin (que ven todo). Sacar a la última
 * persona lo vuelve libre.
 * Solo lo edita el Super Administrador.
 */
export default function PermisosPorPersona() {
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [guardando, setGuardando] = useState('')
  const [nuevoItem, setNuevoItem] = useState('')

  useEffect(() => {
    permisosPorPersonaApi.listar().then(({ data }) => setData(data)).catch(() => setError('No se pudieron cargar los permisos por persona.'))
  }, [])

  // Todos los ítems del menú con su sección, para elegir cuál restringir.
  const opciones = useMemo(() => SECTIONS.filter(s => !s.empresa).flatMap(s => [
    ...(s.items || []).filter(i => !i.llave).map(i => ({ path: i.path, label: `${s.label} · ${i.label}` })),
    ...(s.subsections || []).flatMap(sub => (sub.items || []).map(i => ({ path: i.path, label: `${s.label} · ${sub.label} · ${i.label}` }))),
  ]), [])
  const nombreItem = (path) => opciones.find(o => o.path === path)?.label || path

  const guardar = async (item, ids) => {
    setGuardando(item); setError('')
    try {
      const { data } = await permisosPorPersonaApi.guardar(item, ids)
      setData(data)
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo guardar.')
    } finally { setGuardando('') }
  }

  if (!data) return error ? <p style={st.error}>{error}</p> : null
  const restringidos = data.items
  const libres = opciones.filter(o => !restringidos.some(r => r.item_key === o.path))

  return (
    <div style={st.card}>
      <h3 style={sx.cardTitle}>Permisos por persona</h3>
      <p style={st.nota}>
        Un ítem con personas asignadas lo ven y lo usan <b>solo esas personas</b>, además de los administradores, sin importar el rol
        que tengan. Si sacás a la última persona, el ítem vuelve a seguir el menú por rol.
      </p>
      {error && <p style={st.error}>{error}</p>}

      {restringidos.length === 0 && <p style={st.vacio}>Ningún ítem está restringido a personas.</p>}
      {restringidos.map(r => {
        const ids = r.usuarios.map(u => u.id)
        const disponibles = data.usuarios.filter(u => !ids.includes(u.id))
        return (
          <div key={r.item_key} style={st.fila}>
            <div style={st.item}>
              {nombreItem(r.item_key)}
              {guardando === r.item_key && <span style={st.guardando}> guardando…</span>}
            </div>
            <div style={st.chips}>
              {r.usuarios.map(u => (
                <span key={u.id} style={st.chip}>
                  {u.nombre}
                  <button type="button" style={st.quitar} aria-label={`Quitar a ${u.nombre}`} title={`Quitar a ${u.nombre}`}
                    onClick={() => guardar(r.item_key, ids.filter(x => x !== u.id))}>×</button>
                </span>
              ))}
              <select style={st.select} value="" aria-label={`Agregar persona a ${nombreItem(r.item_key)}`}
                onChange={e => e.target.value && guardar(r.item_key, [...ids, Number(e.target.value)])}>
                <option value="">+ Agregar persona</option>
                {disponibles.map(u => <option key={u.id} value={u.id}>{u.nombre}</option>)}
              </select>
            </div>
          </div>
        )
      })}

      <div style={st.nuevo}>
        <select style={{ ...st.select, minWidth: 280 }} value={nuevoItem} onChange={e => setNuevoItem(e.target.value)} aria-label="Ítem para restringir">
          <option value="">Restringir otro ítem a personas…</option>
          {libres.map(o => <option key={o.path} value={o.path}>{o.label}</option>)}
        </select>
        {nuevoItem && (
          <select style={st.select} value="" aria-label="Primera persona"
            onChange={e => { if (e.target.value) { guardar(nuevoItem, [Number(e.target.value)]); setNuevoItem('') } }}>
            <option value="">Elegí la primera persona</option>
            {data.usuarios.map(u => <option key={u.id} value={u.id}>{u.nombre}</option>)}
          </select>
        )}
      </div>
    </div>
  )
}

const st = {
  card: { ...sx.card, marginBottom: space.xl },
  nota: { fontSize: text.sec, color: colors.textSec, lineHeight: 1.5, margin: `${space.sm}px 0 ${space.lg}px` },
  error: { color: colors.err, fontSize: text.sec, margin: `0 0 ${space.md}px` },
  vacio: { fontSize: text.body, color: colors.textMeta, margin: `0 0 ${space.md}px` },
  fila: { display: 'flex', gap: space.lg, alignItems: 'center', flexWrap: 'wrap', padding: `${space.md}px 0`, borderTop: `1px solid ${colors.borderSoft}` },
  item: { fontSize: text.body, fontWeight: 600, color: colors.text, minWidth: 260 },
  guardando: { fontWeight: 400, color: colors.textMeta, fontSize: text.sec },
  chips: { display: 'flex', gap: space.sm, alignItems: 'center', flexWrap: 'wrap' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: 4, background: colors.linkBg, color: colors.link, borderRadius: radius.pill, padding: '3px 4px 3px 10px', fontSize: text.sec, fontWeight: 500 },
  quitar: { border: 'none', background: 'transparent', color: colors.link, cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: '0 4px', fontFamily: 'inherit' },
  select: { padding: '4px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: text.sec, background: colors.bg, color: colors.textSub, fontFamily: 'inherit' },
  nuevo: { display: 'flex', gap: space.md, flexWrap: 'wrap', marginTop: space.md, paddingTop: space.md, borderTop: `1px solid ${colors.borderSoft}` },
}
